import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, IndianRupee } from 'lucide-react'
import { toast } from 'react-toastify';
import useLedger from '../../hooks/useLedger';
import useBank from '../../hooks/useBank';
import Modal from '../../components/Modal';
import PaginationButtons from '../../components/PaginationButtons';
const BillManagement = () => {
    const { partyId } = useParams();
    const { loading, getPartyById, addBill, getBillsById, addPartyPayment, updateBillStatus } = useLedger();
    const { getBankDetails } = useBank();

    const [partyDetails, setPartyDetails] = useState(null);
    const [bankList, setBankList] = useState([]);
    const [billList, setBillList] = useState([]);
    const [showAddBillModal, setShowAddBillModal] = useState(false);
    const [showMore, setShowMore] = useState(false);
    const [showAddPaymentModal, setAddPaymentModal] = useState(false);
    const [isBillListLoading, setBillListLoading] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const [billsPerPage, setbillsPerPage] = useState(10);

    const [addPaymentForm, setAddPaymentForm] = useState({
        partyId: '',
        date: '',
        amount: 0,
        mode: '',
        status: '',
        referenceNo: '',
        bankId: ''
    });
    const [formData, setFormData] = useState({
        billNo: '',
        billDate: '',
        totalAmount: 0,
        note: '',
        addInitialPayment: false,

        initialPayment: {
            amount: 0,
            date: '',
            referenceNo: '',
            status: '',
            mode: '',
            bankId: ''
        }
    })

    const navigate = useNavigate();

    const fetchUserDetails = async () => {
        try {
            const partyData = await getPartyById(partyId);
            setPartyDetails(partyData.data); // Fixed data structure issue
            // toast.success(partyData.message);
        } catch (err) {
            toast.error(err.message);
        }
    }
    const fetchBillList = async () => {
        try {
            const billsData = await getBillsById(partyId);
            setBillList(billsData.data);

        } catch (err) {
            toast.error(err.message);
        }
    }

    const fetchBankList = async () => {
        try {
            const response = await getBankDetails();
            setBankList(response.banks || []);
        } catch (error) {
            console.log("Error fetching banks:", error);
            setBankList([]);
        }
    }


    const handleUpdateBillStatus = async (billId, status) => {
        try {
            const result = await updateBillStatus(billId, status);
            toast.success(result.message);
            fetchBillList();
        } catch (err) {
            toast.error(err.message);
        }
    }

    const handleCheckbox = (e) => {
        const isChecked = e.target.checked;
        setFormData((prev) => (
            { ...prev, addInitialPayment: isChecked }
        ))
    }
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePaymentChange = (e) => {
        const { name, value } = e.target;
        if (showAddPaymentModal) {
            setAddPaymentForm((prev) => ({ ...prev, [name]: value }));
        } else {
            setFormData((prev) => ({
                ...prev,
                initialPayment: { ...prev.initialPayment, [name]: value }
            }));
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        let payload = {
            partyId: partyId,
            billNo: formData.billNo,
            billDate: formData.billDate,
            totalAmount: Number(formData.totalAmount),
            note: formData.note
        }

        if (formData.addInitialPayment) {
            payload.initialPayment = {
                amount: Number(formData.initialPayment.amount),
                date: formData.initialPayment.date,
                mode: formData.initialPayment.mode,
                status: formData.initialPayment.status,
                referenceNo: formData.initialPayment.referenceNo,
                bankId: formData.initialPayment.bankId
            };
        }

        setBillListLoading(true);
        try {
            const billData = await addBill(payload);
            toast.success(billData.message);
            setBillList((prev) => ([billData.data, ...prev]));
            setShowAddBillModal(false);

            // Refetch party details to update totals if needed (though adding bill only affects Billed Amount)
            fetchUserDetails();

        } catch (err) {
            toast.error(err.message);
        } finally {
            setBillListLoading(false);
        }

    }

    const handleAddPayment = async (e) => {
        e.preventDefault();
        try {
            // Find selected bank and check balance
            const selectedBank = bankList.find(b => b._id === addPaymentForm.bankId);
            if (selectedBank && selectedBank.balance < addPaymentForm.amount) {
                return toast.error(`Insufficient funds! The payment amount (₹${addPaymentForm.amount}) exceeds the current balance (₹${selectedBank.balance}).`);
            }

            const paymentData = await addPartyPayment({
                ...addPaymentForm,
                partyId: partyId
            });

            console.log(paymentData);
            setAddPaymentModal(false);
            toast.success(paymentData.message || 'Payment added');
            fetchUserDetails(); // Update Totals

        } catch (err) {
            toast.error(err.message);
        }
    }

    // KPI Calculations
    const { totalBilledAmount, totalPaidAmount, totalRemainingAmount } = useMemo(() => {
        if (!billList) return { totalBilledAmount: 0, totalPaidAmount: 0, totalRemainingAmount: 0 };

        const activeBills = billList.filter(bill => bill.status !== 'Cancelled');

        const totalBilled = activeBills.reduce((acc, bill) => acc + (bill.totalAmount || 0), 0);
        // FOR GLOBAL PAYMENT: Use party's total paid amount
        const totalPaid = partyDetails?.totalPaidAmount || 0;

        return {
            totalBilledAmount: totalBilled,
            totalPaidAmount: totalPaid,
            totalRemainingAmount: totalBilled - totalPaid
        };
    }, [billList, partyDetails]);

    const filterdBills = useMemo(() => {
        let updatedList = billList || [];

        // search term filtering 
        if (searchTerm) {
            const lowerCaseSearch = searchTerm.toLowerCase();
            updatedList = updatedList.filter(bill =>
                bill.billNo.toLowerCase().includes(lowerCaseSearch) ||
                (bill.note && bill.note.toLowerCase().includes(lowerCaseSearch))
            );
        }
        return updatedList;
    }, [billList, searchTerm]);

    //filteration logic
    const { totalBills, currentBills } = useMemo(() => {
        const totalBills = filterdBills.length;
        const lastIndex = currentPage * billsPerPage;
        const firstIndex = lastIndex - billsPerPage;
        const currentBills = filterdBills.slice(firstIndex, lastIndex);

        return {
            totalBills,
            currentBills
        }
    }, [filterdBills, currentPage, billsPerPage]);

    const getStatusStyle = (status) => {
        switch (status?.toLowerCase()) {
            case 'paid':
            case 'completed':
                return 'bg-emerald-100 text-emerald-700 border-emerald-200';
            case 'partial':
                return 'bg-amber-100 text-amber-700 border-amber-200';
            case 'pending':
                return 'bg-rose-100 text-rose-700 border-rose-200';
            default:
                return 'bg-gray-100 text-gray-700 border-gray-200';
        }
    };
    useEffect(() => {
        fetchUserDetails();
        fetchBillList();
        fetchBankList();

    }, [partyId])

    console.log(partyDetails);
    console.log(billList);
    if (loading || !partyDetails) {
        return (
            <div className="min-h-screen flex justify-center items-center bg-gray-50">
                <p className="text-gray-500 text-lg">Loading party details...</p>
            </div>
        )
    }
    return (
        <div className='w-full'>
            {/* ... (Back Button) ... */}
            <div className="bg-red-300 relative mb-0">
                {/* Back Button */}
                <button
                    onClick={() => navigate("/dashboard/parties")}
                    className="absolute -top-5 -left-4 md:-top-6 md:-left-5 flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors group"
                >
                    <ChevronLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                    <span className="font-medium">Back to Parties</span>
                </button>
            </div>
            {/* --- Header --- */}
            <div className='w-full pt-2 mx-auto mb-6'>
                <h1 className='text-3xl font-bold text-gray-800'>Bills Management</h1>
                <p className='text-gray-500 mt-1'>Manage bills and payments for <span className="font-semibold text-gray-800">{partyDetails?.name}</span></p>
            </div>

            {/* --- Main Info Container (Stacked Vertically) --- */}
            <div className='w-full bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden'>

                {/* --- SECTION 1: Party Details (Unchanged) --- */}
                <div className='p-4 border-b border-gray-100'>
                    <h2 className='text-lg font-bold text-gray-700 mb-4'>Party Details</h2>

                    <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
                        {/* Name */}
                        <div className='flex flex-col gap-1'>
                            <label className='text-xs font-semibold text-gray-500 uppercase tracking-wide'>Party Name</label>
                            <div className='font-medium text-gray-800 text-lg'>{partyDetails?.name}</div>
                        </div>

                        {/* Contact */}
                        <div className='flex flex-col gap-1'>
                            <label className='text-xs font-semibold text-gray-500 uppercase tracking-wide'>Contact Number</label>
                            <div className='font-medium text-gray-800 text-lg tracking-wide'>{partyDetails.contactNumber}</div>
                        </div>

                        {/* Category */}
                        <div className='flex flex-col gap-1'>
                            <label className='text-xs font-semibold text-gray-500 uppercase ml-2 tracking-wide mb-1'>Category</label>
                            <div>
                                <span className='bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm font-medium'>
                                    {partyDetails.category}
                                </span>
                            </div>
                        </div>

                        {/* Address */}
                        <div className='flex flex-col gap-1 md:col-span-2'>
                            <label className='text-xs font-semibold text-gray-500 uppercase tracking-wide'>Address</label>
                            <div className='text-gray-700 leading-relaxed'>
                                {partyDetails.address}
                            </div>
                        </div>
                    </div>
                    <p
                        onClick={() => setShowMore(!showMore)}
                        className='text-right text-blue-700 cursor-pointer font-semibold '>show more...</p>
                </div>

                {/* --- SECTION 2: Bank Details (Styled like Section 1) --- */}
                {showMore && <div className='p-6 bg-gray-50 border-t border-gray-100 animate-in fade-in slide-in-from-top-4 duration-300'>
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
                        <h2 className='text-xl font-bold text-gray-700 flex items-center gap-2'>
                            <span className="p-2 bg-white rounded-lg shadow-sm border border-gray-100">🏦</span> Party Bank Accounts
                        </h2>
                    </div>

                    {!partyDetails.bankDetails || partyDetails.bankDetails.length === 0 ? (
                        <div className="text-center py-6 text-gray-400 bg-white rounded-xl border border-dashed border-gray-200">
                            No bank details available for this party.
                        </div>
                    ) : (
                        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
                            {partyDetails.bankDetails.map((bank, index) => (
                                <div key={index} className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow relative overflow-hidden group">
                                    <div className="absolute top-0 right-0 w-16 h-16 bg-blue-50 rounded-bl-full -mr-8 -mt-8 opacity-50 group-hover:opacity-100 transition-opacity"></div>

                                    <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3 pb-2 border-b border-gray-100 flex items-center gap-2">
                                        Account #{index + 1}
                                    </h3>

                                    <div className="space-y-3">
                                        {/* Bank Name */}
                                        <div className='flex flex-col'>
                                            <span className='text-xs text-gray-400'>Bank Name</span>
                                            <span className='font-semibold text-gray-800 text-lg'>
                                                {bank.bankName || "N/A"}
                                            </span>
                                        </div>

                                        {/* Account Number */}
                                        <div className='flex flex-col'>
                                            <span className='text-xs text-gray-400'>Account Number</span>
                                            <span className='font-mono text-gray-700 font-medium tracking-wide bg-gray-50 px-2 py-1 rounded w-fit'>
                                                {bank.accountNumber || "N/A"}
                                            </span>
                                        </div>

                                        {/* IFSC Code */}
                                        <div className='flex flex-col'>
                                            <span className='text-xs text-gray-400'>IFSC Code</span>
                                            <span className='font-mono text-gray-600 text-sm'>
                                                {bank.ifsc || "N/A"}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>}

            </div>
            {/* add bill button  */}
            <div className='text-right mt-4 mb-8'>
                <button
                    onClick={() => {
                        setFormData({
                            billNo: '',
                            billDate: '',

                            totalAmount: 0,
                            paidAmount: 0,
                            note: '',
                            addInitialPayment: false,

                            initialPayment: {
                                amount: 0,
                                date: '',
                                referenceNo: '',
                                status: '',
                                mode: '',
                                bankId: ''
                            }
                        })
                        setShowAddBillModal(true)
                    }}
                    className='rounded-xl text-white px-6 py-3 bg-blue-600 hover:bg-blue-700'>+ Add Bill</button>
            </div>

            {/* --- KPI Cards Section --- */}
            <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-8'>
                {/* Total Billed Card */}
                <div className='bg-white p-5 rounded-xl border border-blue-100 shadow-sm flex items-center justify-between'>
                    <div>
                        <p className='text-sm font-semibold text-gray-500 uppercase tracking-wide'>Total Billed</p>
                        <h3 className='text-2xl font-bold text-gray-800 mt-1'>₹{totalBilledAmount.toLocaleString('INR')}</h3>
                    </div>
                    <div className='p-3 bg-blue-50 text-blue-600 rounded-full'>
                        <span className='text-2xl'>📄</span>
                    </div>
                </div>

                {/* Total Paid Card */}
                <div className='bg-white p-5 rounded-xl border border-emerald-100 shadow-sm flex items-center justify-between'>
                    <div>
                        <p className='text-sm font-semibold text-gray-500 uppercase tracking-wide'>Total Paid</p>
                        <h3 className='text-2xl font-bold text-emerald-600 mt-1'>₹{totalPaidAmount.toLocaleString('INR')}</h3>
                    </div>
                    <div className='p-3 bg-emerald-50 text-emerald-600 rounded-full'>
                        <span className='text-2xl'>💰</span>
                    </div>
                </div>

                {/* Total Pending Card */}
                <div className='bg-white p-5 rounded-xl border border-rose-100 shadow-sm flex items-center justify-between'>
                    <div>
                        <p className='text-sm font-semibold text-gray-500 uppercase tracking-wide'>Total Pending</p>
                        <h3 className='text-2xl font-bold text-rose-600 mt-1'>₹{totalRemainingAmount.toLocaleString('INR')}</h3>
                    </div>

                    {/* Pay Button inside KPI */}
                    <button
                        onClick={() => {
                            setAddPaymentForm((prev) => ({
                                ...prev,
                                partyId: partyId,
                                bankId: '',
                                amount: '',
                                mode: '',
                                status: '',
                                referenceNo: ''
                            }));
                            setAddPaymentModal(true);
                        }}
                        className="px-4 py-2 bg-rose-50 text-rose-600 hover:bg-rose-100 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 border border-rose-100"
                    >
                        <span>Global Pay</span>
                        <span className="text-lg">💳</span>
                    </button>
                    {/* <div className='p-3 bg-rose-50 text-rose-600 rounded-full'>
                        <span className='text-2xl'>⏳</span>
                    </div> */}
                </div>
            </div>

            <div className="space-y-4">

                <div className='flex justify-between items-center'>
                    <h1 className='text-2xl font-semibold text-gray-700'>Bill's List 📋</h1>
                    <div className='flex items-center gap-3 text-gray-600 border border-gray-100 shadow-sm rounded-sm p-1'>
                        <p className='text-sm'>Bill's per page</p>
                        <select name="noOfBills"
                            onChange={(e) => setbillsPerPage(Number(e.target.value))}
                        >
                            <option value='10'>10</option>
                            <option value='5'>5</option>
                            <option value='15'>15</option>
                            <option value='20'>20</option>
                            <option value={totalBills}>All</option>

                        </select>
                    </div>
                </div>

                {/* --- SEARCH BAR --- */}
                <div className="bg-white p-4 rounded-t-xl shadow-sm border border-gray-200">
                    <input
                        type="text"
                        placeholder="Search by Bill No or Note..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                    />
                </div>

            </div>

            {/* table  */}
            {/* <h1 className='text-2xl font-semibold text-gray-700 mb-2'>Bill's List 📋</h1> */}
            <div className='bg-white shadow-sm rounded-b-xl border border-gray-200 overflow-hidden'>
                <div className="overflow-x-auto">
                    <table className="w-full  text-left border-collapse">

                        {/* Table Head */}
                        <thead className="bg-gray-50/50 border-b border-gray-200">
                            <tr>
                                <th className="py-4 px-6 text-xs font-bold uppercase tracking-wider text-gray-500">SR.NO</th>
                                <th className="py-4 px-6 text-xs font-bold uppercase tracking-wider text-gray-500">Bill No</th>
                                <th className="py-4 px-6 text-xs font-bold uppercase tracking-wider text-gray-500">Date</th>

                                <th className="py-4 px-6 text-xs font-bold uppercase tracking-wider text-gray-500">Note</th>
                                <th className="py-4 px-6 text-xs font-bold uppercase tracking-wider text-gray-500 text-right">Total</th>
                                <th className="py-4 px-6 text-xs font-bold uppercase tracking-wider text-gray-500 text-center">Actions</th>
                            </tr>
                        </thead>

                        {/* Table Body */}
                        <tbody className="divide-y divide-gray-100">
                            {isBillListLoading ? (
                                <tr>
                                    <td colSpan="8" className="py-10 text-center text-gray-400 italic">
                                        Loading records...
                                    </td>
                                </tr>
                            ) : (filterdBills?.length == 0 ?
                                (
                                    <tr>
                                        <td colSpan="8" className="py-10 text-center text-gray-400 italic">
                                            No bill's found
                                        </td>
                                    </tr>
                                ) : (currentBills?.map((bill, index) => {



                              


                                    // Simplified Logic: Allow clearing if Party's Total Paid Amount is sufficient for this bill
                                    const totalPartyPaid = partyDetails?.totalPaidAmount || 0;
                                    const canClear = totalPartyPaid >= bill.totalAmount;

                                    return (
                                        <tr key={bill._id} className={`hover:bg-gray-50 transition-colors group ${bill.status === 'Cancelled' || bill.status === 'Paid' ? '!text-gray-400 font-semibold  bg-gray-50 italic line-through' : ''}`}>

                                            {/* Sr No */}
                                            <td className="py-4 px-6  text-sm text-gray-400 font-mono">
                                                {index + 1}
                                            </td>

                                            {/* Bill No */}
                                            <td className="py-4 px-6">
                                                <p className="font-semibold text-gray-800 text-sm">{bill.billNo}</p>
                                            </td>
                                            {/* Date */}
                                            <td className="py-4 px-6 text-gray-600 text-sm font-mono whitespace-nowrap">
                                                {new Date(bill.billDate).toLocaleDateString('en-GB')}
                                            </td>

                                            {/* //note */}
                                            <td className="py-4 px-6">
                                                <p className="font-semibold text-gray-800 text-sm">{bill.note}</p>
                                            </td>

                                            {/* Total Amount */}
                                            <td className="py-4 px-6 text-right font-mono text-sm font-medium text-gray-900">
                                                {bill.totalAmount.toLocaleString('INR')}
                                            </td>

                                            {/* Actions */}
                                            <td className="py-4 px-6">
                                                <div className="flex items-center justify-center gap-3">
                                                    {bill.status !== 'Paid' && bill.status !== 'Cancelled' ? (
                                                        <button
                                                            onClick={() => handleUpdateBillStatus(bill._id, 'Paid')}
                                                            // Logic: Allow if specific bill is paid OR if Party Total Paid >= Bill Amount
                                                            disabled={!canClear && bill.paidAmount < bill.totalAmount}
                                                            className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-lg transition-all text-xs font-semibold shadow-sm 
                                                            ${(canClear || bill.paidAmount >= bill.totalAmount)
                                                                    ? 'bg-white text-emerald-600 border-emerald-200 hover:bg-emerald-50 hover:shadow-md cursor-pointer'
                                                                    : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-70'
                                                                }`}
                                                            title={(canClear || bill.paidAmount >= bill.totalAmount)
                                                                ? "Mark as Cleared"
                                                                : `Total Paid (₹${totalPartyPaid.toLocaleString()}) < Bill Amount`}
                                                        >
                                                            <span className="text-lg leading-none">✓</span>
                                                            <span>Clear</span>
                                                        </button>
                                                    ) : (
                                                        <span className="text-xs text-gray-400 italic">
                                                            {bill.status === 'Cancelled' ? 'Cancelled' : '—'}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                })
                                ))}

                        </tbody>
                    </table>
                    <PaginationButtons
                        totalRecord={totalBills}
                        recordPerPage={billsPerPage}
                        currentPage={currentPage}
                        setCurrentPage={(pageToset) => setCurrentPage(pageToset)}
                        setNextPage={() => setCurrentPage((prev) => (Math.min(totalBills, prev + 1)))}
                        setPrevPage={() => setCurrentPage((prev) => (Math.max(1, prev - 1)))}
                    />
                </div>
            </div>

            {/* add bill modal  */}
            <Modal
                isOpen={showAddBillModal}
                onClose={() => setShowAddBillModal(false)}
                title={'Add Bill'}
            >
                <form onSubmit={handleSubmit} className="space-y-6">

                    {/* --- SECTION 1: BILL DETAILS --- */}
                    <div>
                        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4 border-b pb-2">
                            Bill Information
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                            {/* Bill No */}
                            <div className="flex flex-col gap-1">
                                <label className="text-sm font-semibold text-gray-700">Bill Number <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    name="billNo"
                                    value={formData.billNo}
                                    onChange={handleChange}
                                    placeholder="e.g. INV-2025-001"
                                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-gray-200 outline-none transition-all"
                                    required
                                />
                            </div>

                            {/* Bill Date */}
                            <div className="flex flex-col gap-1">
                                <label className="text-sm font-semibold text-gray-700">Bill Date <span className="text-red-500">*</span></label>
                                <input
                                    type="date"
                                    name="billDate"
                                    value={formData.billDate}
                                    onChange={handleChange}
                                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-gray-200 outline-none transition-all"
                                    required
                                />
                            </div>



                            {/* Total Amount */}
                            <div className="flex flex-col gap-1">
                                <label className="text-sm font-semibold text-gray-700">Total Amount (₹) <span className="text-red-500">*</span></label>
                                <input
                                    type="number"
                                    name="totalAmount"
                                    value={formData.totalAmount}
                                    onChange={handleChange}
                                    placeholder="0.00"
                                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-gray-200 outline-none transition-all"
                                    required
                                    min="0"
                                />
                            </div>

                            {/* Note (Full Width) */}
                            <div className="flex flex-col gap-1 md:col-span-2">
                                <label className="text-sm font-semibold text-gray-700">Note / Description</label>
                                <textarea
                                    name="note"
                                    rows="2"
                                    value={formData.note}
                                    onChange={handleChange}
                                    placeholder="Add details about items..."
                                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-gray-200 outline-none resize-none"
                                ></textarea>
                            </div>
                        </div>
                    </div>

                    {/* --- SECTION 2: INITIAL PAYMENT TOGGLE --- */}
                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-center justify-between">
                        <div>
                            <h4 className="text-sm font-bold text-gray-800">Add Initial Payment?</h4>
                            <p className="text-xs text-gray-500 mt-0.5">Check this if you have made an advance or full payment for this bill.</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={formData.addInitialPayment}
                                onChange={handleCheckbox}
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                    </div>

                    {/* --- SECTION 3: PAYMENT DETAILS (CONDITIONAL) --- */}
                    {formData.addInitialPayment && (
                        <div className="animate-in fade-in slide-in-from-top-4 duration-300 bg-gray-50 p-5 rounded-xl border border-gray-200">
                            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4 border-b border-gray-200 pb-2">
                                Payment Details
                            </h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                                {/* Bank Selection */}
                                <div className="flex flex-col gap-1">
                                    <label className="text-sm font-medium text-gray-700">Select Bank Account <span className="text-red-500">*</span></label>
                                    <select
                                        name="bankId"
                                        value={formData.initialPayment.bankId}
                                        onChange={handlePaymentChange}
                                        className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-100 outline-none bg-white"
                                        required={formData.addInitialPayment}
                                    >
                                        <option value="" disabled hidden>Select Bank</option>
                                        {bankList.map((bank) => (
                                            <option key={bank._id} value={bank._id}>
                                                {bank.accountName} - {bank.accountNumber.slice(-4)}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Payment Amount */}
                                <div className="flex flex-col gap-1">
                                    <label className="text-sm font-medium text-gray-700">Paid Amount (₹) <span className="text-red-500">*</span></label>
                                    <input
                                        min={0}
                                        type="number"
                                        name="amount"
                                        value={formData.initialPayment.amount}
                                        onChange={handlePaymentChange}
                                        className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-100 outline-none"
                                        required={formData.addInitialPayment}
                                    />
                                </div>

                                {/* Payment Mode */}
                                <div className="flex flex-col gap-1">
                                    <label className="text-sm font-medium text-gray-700">Payment Mode</label>
                                    <select
                                        name="mode"
                                        value={formData.initialPayment.mode}
                                        onChange={handlePaymentChange}
                                        className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-100 outline-none bg-white"
                                    >
                                        <option value="" disabled hidden>select payment mode</option>
                                        <option value="Cash">Cash 💵</option>
                                        <option value="UPI">UPI / Online 📱</option>
                                        <option value="Check">Check 🏦</option>
                                    </select>
                                </div>

                                {/* Payment Date */}
                                <div className="flex flex-col gap-1">
                                    <label className="text-sm font-medium text-gray-700">Payment Date</label>
                                    <input
                                        type="date"
                                        name="date"
                                        value={formData.initialPayment.date}
                                        onChange={handlePaymentChange}
                                        className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-100 outline-none"
                                    />
                                </div>

                                {/* Reference No (Shows only if NOT Cash) */}
                                {formData.initialPayment.mode !== 'Cash' && (
                                    <div className="flex flex-col gap-1">
                                        <label className="text-sm font-medium text-gray-700">Reference / Check No</label>
                                        <input
                                            type="text"
                                            name="referenceNo"
                                            value={formData.initialPayment.referenceNo}
                                            onChange={handlePaymentChange}
                                            placeholder="e.g. UPI Ref ID or Check No"
                                            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-100 outline-none"
                                        />
                                    </div>
                                )}

                                {/* Status */}
                                <div className="flex flex-col gap-1">
                                    <label className="text-sm font-medium text-gray-700">Payment Status</label>
                                    <select
                                        name="status"
                                        value={formData.initialPayment.status}
                                        onChange={handlePaymentChange}
                                        className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-100 outline-none bg-white"
                                    >
                                        <option value="" disabled hidden>select payment status</option>
                                        <option value="Completed">Completed ✅</option>
                                        <option value="Pending">Pending ⏳</option>
                                        {/* <option value="Failed">Failed ❌</option> */}
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* --- ACTION BUTTONS --- */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={() => setShowAddBillModal(false)}
                            className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-6 py-2.5 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-black shadow-sm transition-all transform active:scale-95"
                        >
                            Save Bill
                        </button>
                    </div>
                </form>
            </Modal>

            {/* showPayments modal  - REMOVED */}

            {/* --- Sticky Footer for Global Payment - REMOVED --- */}


            {/* add payment modal  */}
            <Modal
                title={'Add Payment'}
                isOpen={showAddPaymentModal}
                onClose={() => setAddPaymentModal(false)}
            >
                <div>
                    <form onSubmit={handleAddPayment} className="space-y-6">
                        <div className="bg-gray-50 p-5 rounded-xl border border-gray-200 mb-4">
                            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4 border-b border-gray-200 pb-2">
                                Payment Details
                            </h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                {/* Bank Selection */}
                                <div className="flex flex-col gap-1">
                                    <label className="text-sm font-medium text-gray-700">Select Bank Account <span className="text-red-500">*</span></label>
                                    <select
                                        name="bankId"
                                        value={addPaymentForm.bankId}
                                        onChange={handlePaymentChange}
                                        className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-100 outline-none bg-white"
                                        required
                                    >
                                        <option value="" disabled hidden>Select Bank</option>
                                        {bankList.map((bank) => (
                                            <option key={bank._id} value={bank._id}>
                                                {bank.accountName} - {bank.accountNumber.slice(-4)}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Payment Amount */}
                                <div className="flex flex-col gap-1">
                                    <label className="text-sm font-medium text-gray-700">Paid Amount (₹) <span className="text-red-500">*</span></label>
                                    <input
                                        min={0}
                                        type="number"
                                        name="amount"
                                        value={addPaymentForm.amount}
                                        onChange={handlePaymentChange}
                                        className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-100 outline-none"
                                        required={true}
                                    />
                                </div>

                                {/* Payment Mode */}
                                <div className="flex flex-col gap-1">
                                    <label className="text-sm font-medium text-gray-700">Payment Mode</label>
                                    <select
                                        name="mode"
                                        value={addPaymentForm.mode}
                                        onChange={handlePaymentChange}
                                        className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-100 outline-none bg-white"
                                    >
                                        <option value="" disabled hidden>select payment mode</option>
                                        <option value="Cash">Cash 💵</option>
                                        <option value="UPI">UPI / Online 📱</option>
                                        <option value="Check">Check 🏦</option>
                                    </select>
                                </div>

                                {/* Payment Date */}
                                <div className="flex flex-col gap-1">
                                    <label className="text-sm font-medium text-gray-700">Payment Date</label>
                                    <input
                                        type="date"
                                        name="date"
                                        value={addPaymentForm.date}
                                        onChange={handlePaymentChange}
                                        className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-100 outline-none"
                                    />
                                </div>

                                {/* Reference No (Shows only if NOT Cash) */}
                                {addPaymentForm.mode !== 'Cash' && (
                                    <div className="flex flex-col gap-1">
                                        <label className="text-sm font-medium text-gray-700">Reference / Check No</label>
                                        <input
                                            type="text"
                                            name="referenceNo"
                                            value={addPaymentForm.referenceNo}
                                            onChange={handlePaymentChange}
                                            placeholder="e.g. UPI Ref ID or Check No"
                                            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-100 outline-none"
                                        />
                                    </div>
                                )}

                                {/* Status */}
                                <div className={`flex flex-col gap-1`}>
                                    <label className="text-sm font-medium text-gray-700">Payment Status</label>
                                    <select
                                        name="status"
                                        value={addPaymentForm.status}
                                        onChange={handlePaymentChange}
                                        className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-100 outline-none bg-white"
                                    >
                                        <option value="" disabled hidden>select payment status</option>
                                        <option value="Completed">Completed ✅</option>
                                        <option value="Pending">Pending ⏳</option>
                                        {/* <option value="Failed">Failed ❌</option> */}
                                    </select>
                                </div>
                                {/* --- ACTION BUTTONS --- */}
                                <div className={`flex justify-end gap-4  md:col-span-2 pt-4 border-t border-gray-100`}>
                                    <button
                                        type="button"
                                        onClick={() => setAddPaymentModal(false)}
                                        className="px-6 py-2 md:px-5 md:py-0.75 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-6 py-2 md:px-5 md:py-0.75 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-black shadow-sm transition-all transform active:scale-95"
                                    >
                                        Save Bill
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </Modal>

        </div>
    )
}

export default BillManagement;